import { AccountTypes } from '@interfaces/models/account-types';
import { IndividualDesignationListedTypes } from '@interfaces/models/individual-designation-types';
import { IRADesignationListedTypes } from '@interfaces/models/ira-designation-types';
import { TriageState as _triageState } from '@interfaces/state';
import { Action, createReducer, on } from '@ngrx/store';
import {
  ExperimentGroupEnum,
  FlowTypes,
  GiftingDesignationTypes,
  IRATrustTypes,
  ISelectedClientAccountsState,
  YesNoEmpty,
} from '@shared/models';

import {
  ClearTriageState,
  UpdateAccountTypeSelection,
  UpdateClientStatusSelection,
  UpdateFlowTypeSelection,
  UpdateGiftingDesignation,
  UpdateIndividualDesignation,
  UpdateIRADesignation,
  UpdateIsSingleTrustee,
  UpdateSpouseStatusSelection,
  UpdateTriageSelections,
  UpdateTrustTriageSelections,
} from '../../actions';
import { StartOverStateCleared } from '../../actions/start-over/start-over.actions';
import { FeatureToggleState } from '../feature/feature.reducer';

export interface TriageState extends _triageState {}

export const initialState: TriageState = {
  isClient: '',
  isSpouse: '',
  isDecedentMarried: YesNoEmpty.EMPTY,
  isSingleExecutor: YesNoEmpty.EMPTY,
  isExecutorDecedentsSpouse: YesNoEmpty.EMPTY,
  accountTypeInheriting: AccountTypes.EMPTY,
  IRADesignation: IRADesignationListedTypes.EMPTY,
  individualDesignation: IndividualDesignationListedTypes.EMPTY,
  giftingDesignation: GiftingDesignationTypes.EMPTY,
  flowType: FlowTypes.EMPTY,
  experimentGroup: ExperimentGroupEnum.NONE,
  isTrusteeOrg: YesNoEmpty.EMPTY,
  isSingleTrustee: YesNoEmpty.EMPTY,
  typeOfTrust: IRATrustTypes.EMPTY,
  isUpdatingTrust: false,
  isUpdatingTrustee: false,
  isSuccessorOrLivingTrustee: YesNoEmpty.EMPTY,
  hasPassedAway: YesNoEmpty.EMPTY,
  isTrustReportingUnderDecedentsSSN: YesNoEmpty.EMPTY,
  isTrusteeOrEstate: YesNoEmpty.EMPTY,
  isInheritingMultipleAccounts: YesNoEmpty.EMPTY,
};

const triageReducer = createReducer(
  initialState,
  on(UpdateClientStatusSelection, (state, { payload }) => ({
    ...state,
    isClient: payload,
  })),
  on(UpdateSpouseStatusSelection, (state, { payload }) => ({
    ...state,
    isSpouse: payload,
  })),
  on(UpdateAccountTypeSelection, (state, { payload }) => ({
    ...state,
    accountTypeInheriting: payload,
  })),
  on(UpdateIRADesignation, (state, { payload }) => ({
    ...state,
    IRADesignation: payload,
  })),
  on(UpdateIndividualDesignation, (state, { payload }) => ({
    ...state,
    individualDesignation: payload,
  })),
  on(UpdateGiftingDesignation, (state, { payload }) => ({
    ...state,
    giftingDesignation: payload,
  })),
  on(UpdateFlowTypeSelection, (state, { payload }) => {
    return {
      ...state,
      flowType: payload,
    };
  }),
  on(UpdateTriageSelections, (state, { payload }) => ({
    ...state,
    ...initialState,
    ...payload,
    isSpouse: payload.isSpouse || payload.isExecutorDecedentsSpouse?.toUpperCase() || '',
    flowType: state.flowType,
  })),
  on(UpdateTrustTriageSelections, (state, { payload }) => ({
    ...state,
    ...payload,
  })),
  on(UpdateIsSingleTrustee, (state, { payload }) => ({
    ...state,
    isSingleTrustee: payload,
  })),
  on(ClearTriageState, () => {
    return initialState;
  }),
  on(StartOverStateCleared, () => {
    return initialState;
  }),
);

export function reducer(state: TriageState | undefined, action: Action) {
  return triageReducer(state, action);
}

export const convertRelationshipToIsSpouse = (relationship: string) =>
  'SPOUSE' === relationship ? 'YES' : 'NO';

export const getClientStatusSelection = (state: TriageState) => (state ? state.isClient : null);
export const getSpouseStatusSelection = (state: TriageState) => (state ? state.isSpouse : null);
export const getDecedentMarriageStatusSelection = (state: TriageState) =>
  state ? state.isDecedentMarried : null;
export const getSingleExecutorStatusSelection = (state: TriageState) =>
  state ? state.isSingleExecutor : null;
export const getExecutorDecedentsSpouseStatusSelection = (state: TriageState) =>
  state ? state.isExecutorDecedentsSpouse : null;
export const getAccountTypeSelection = (state: TriageState) =>
  state ? state.accountTypeInheriting : null;
export const getIRADesignationSelection = (state: TriageState) =>
  state ? state.IRADesignation : null;
export const getIndividualDesignationSelection = (state: TriageState) =>
  state ? state.individualDesignation : null;
export const getGiftingDesignationSelection = (state: TriageState) =>
  state ? state.giftingDesignation : null;
export const getSelectExperimentGroup = (state: TriageState) =>
  state ? state.experimentGroup : null;
export const getFlowTypeSelection = (state: TriageState) => (state ? state.flowType : null);
export const getAccountRegisteredRoles = (state: ISelectedClientAccountsState) =>
  state ? state.transferFrom.selectedAccounts[0]?.accountData?.registeredRoles : null;
export const getAccountGrantedRoles = (state: ISelectedClientAccountsState) =>
  state ? state.transferFrom.selectedAccounts[0]?.accountData?.grantedRoles : null;

export const getIsClient = (state: TriageState) => 'YES' === getClientStatusSelection(state);
export const getIsProspect = (state: TriageState) => 'NO' === getClientStatusSelection(state);
export const getIsSpouse = (state: TriageState) => 'YES' === getSpouseStatusSelection(state);
export const getIsIRASpouse = (state: TriageState) => getIsSpouse(state) && getIsIRAAccount(state);
export const getIsDecedentMarried = (state: TriageState) =>
  YesNoEmpty.YES === getDecedentMarriageStatusSelection(state);
export const getIsSingleExecutor = (state: TriageState) =>
  YesNoEmpty.YES === getSingleExecutorStatusSelection(state);
export const getIsMultiExecutor = (state: TriageState) =>
  YesNoEmpty.NO === getSingleExecutorStatusSelection(state);
export const getIsExecutorDecedentsSpouse = (state: TriageState) =>
  YesNoEmpty.YES === getExecutorDecedentsSpouseStatusSelection(state);
export const getIsNotExecutorDecedentsSpouse = (state: TriageState) =>
  YesNoEmpty.NO === getExecutorDecedentsSpouseStatusSelection(state);
export const getRelationship = (state: TriageState) =>
  getIsSpouse(state) ? 'SPOUSE' : 'NONSPOUSE';
export const getClientStatus = (state: TriageState) => (getIsClient(state) ? 'CLIENT' : 'GUEST');

export const getIsIRAAccount = (state: TriageState) =>
  AccountTypes.IRA === getAccountTypeSelection(state);
export const getIsIndividualAccount = (state: TriageState) =>
  AccountTypes.INDV === getAccountTypeSelection(state);
export const getIsJointAccount = (state: TriageState) =>
  AccountTypes.JOINT === getAccountTypeSelection(state) ||
  AccountTypes.JOINT_TENANTS_WROS === getAccountTypeSelection(state);
export const getIsTrustAccount = (state: TriageState) =>
  AccountTypes.TRUST === getAccountTypeSelection(state);
export const getIsUgmaUtmaAccount = (state: TriageState) =>
  AccountTypes.UGMA_UTMA === getAccountTypeSelection(state);
export const getIsOther = (state: TriageState) =>
  AccountTypes.OTHER === getAccountTypeSelection(state);
export const getIsVanguardGiftingFlow = (state: TriageState) =>
  FlowTypes.GIFTING === getFlowTypeSelection(state) &&
  GiftingDesignationTypes.VANGUARD === getGiftingDesignationSelection(state);
export const getIsNonVanguardGiftingFlow = (state: TriageState) =>
  FlowTypes.GIFTING === getFlowTypeSelection(state) &&
  GiftingDesignationTypes.NON_VANGUARD === getGiftingDesignationSelection(state);
export const getIsInheritanceFlow = (state: TriageState) =>
  FlowTypes.INHERITANCE === getFlowTypeSelection(state);
export const getIsDivorceFlow = (state: TriageState) =>
  FlowTypes.DIVORCE === getFlowTypeSelection(state);
export const getIsMultiInheritanceFlow = (state: TriageState) =>
  FlowTypes.MULTI_INHERITANCE === getFlowTypeSelection(state);
export const getIsGiftingFlow = (state: TriageState) =>
  getIsNonVanguardGiftingFlow(state) || getIsVanguardGiftingFlow(state);
export const getIsInternalTransferFlow = (state: TriageState) =>
  FlowTypes.INTERNAL_TRANSFER === getFlowTypeSelection(state);
export const getIsMinorFlow = (state: TriageState) =>
  FlowTypes.MINOR === getFlowTypeSelection(state);
export const getIsIRASingleExecutor = (state: TriageState) =>
  getIsIRAAccount(state) && getIsIRAExecutor(state) && getIsSingleExecutor(state);
export const getIsIndividualSingleExecutor = (state: TriageState) =>
  getIsIndividualAccount(state) && getIsIndividualExecutor(state) && getIsSingleExecutor(state);
export const getIsIRASpouseExecutor = (state: TriageState) =>
  getIsIRAAccount(state) && getIsIRAExecutor(state) && getIsExecutorDecedentsSpouse(state);
export const getIsIRAExecutor = (state: TriageState) =>
  IRADesignationListedTypes.EXECUTOR === getIRADesignationSelection(state);
export const getIsIRATrust = (state: TriageState) =>
  IRADesignationListedTypes.TRUSTEE === getIRADesignationSelection(state);
export const getIsIndividualExecutor = (state: TriageState) =>
  IndividualDesignationListedTypes.EXECUTOR === getIndividualDesignationSelection(state);
export const getIsExecutor = (state: TriageState) =>
  getIsIRASingleExecutor(state) ||
  IndividualDesignationListedTypes.EXECUTOR === getIndividualDesignationSelection(state);
export const getIsIRAListedByRelationship = (state: TriageState) =>
  IRADesignationListedTypes.RELATIONSHIP === getIRADesignationSelection(state);
export const isIneligibleIRADesignation = (
  state: TriageState,
  featureToggleState: FeatureToggleState,
) =>
  (IRADesignationListedTypes.EXECUTOR === getIRADesignationSelection(state) &&
    !featureToggleState.cwp_ira_scale_estate) ||
  IRADesignationListedTypes.NOTLISTED === getIRADesignationSelection(state) ||
  IRADesignationListedTypes.NOTSURE === getIRADesignationSelection(state);
export const getIsIndividualGifting = (state: TriageState) =>
  getIsIndividualAccount(state) && getIsGiftingFlow(state);
export const getIsTrustGifting = (state: TriageState) =>
  getIsTrustAccount(state) && getIsGiftingFlow(state);
export const getIsScaleGifting = (state: TriageState) =>
  getIsGiftingFlow(state) && (getIsTrustAccount(state) || getIsJointAccount(state));

export const getVanguardGiftingScaleStatus = (state: TriageState) => {
  return getIsVanguardGiftingFlow(state) && (getIsTrustAccount(state) || getIsJointAccount(state));
};

export const getIsTrusteeOrg = (state: TriageState) => {
  return state.isTrusteeOrg === YesNoEmpty.YES;
};

export const getIsOnlyTrustee = (state: TriageState) => {
  return state.isSingleTrustee === YesNoEmpty.YES;
};

export const getIsIRAToEstateFlow = (state: TriageState) => {
  if (!state) {
    return null;
  }
  return (
    state.IRADesignation === IRADesignationListedTypes.EXECUTOR &&
    state.isSingleExecutor === YesNoEmpty.YES &&
    state.accountTypeInheriting === AccountTypes.IRA &&
    state.flowType === FlowTypes.INHERITANCE
  );
};

export const getIsTrustToTrustFlow = (state: TriageState) => {
  if (!state) {
    return false;
  }
  return (
    !!state.isUpdatingTrust &&
    state.hasPassedAway === YesNoEmpty.YES &&
    state.accountTypeInheriting === AccountTypes.TRUST &&
    state.isSuccessorOrLivingTrustee === YesNoEmpty.YES
  );
};

export const getTypeOfTrust = (state: TriageState) => {
  return state.typeOfTrust;
};

export const getIsTrustToTrust = (state: TriageState) => {
  return state.isUpdatingTrust || state.isUpdatingTrustee;
};

export const getIsUpdatingTrust = (state: TriageState) => {
  return state.isUpdatingTrust;
};

export const getIsUpdatingTrustee = (state: TriageState) => {
  return state.isUpdatingTrustee;
};

export const getIsSuccessorOrLivingTrustee = (state: TriageState) => {
  return state.isSuccessorOrLivingTrustee;
};

export const getHasPassedAway = (state: TriageState) => {
  return state.hasPassedAway;
};

export const getIsTrustReportingUnderDecedentsSSN = (state: TriageState) => {
  return state.isTrustReportingUnderDecedentsSSN;
};

export const getIsTrusteeOrEstate = (state: TriageState) => state.isTrusteeOrEstate;

export const getIsMultiInheritanceAvailable = (state: TriageState): boolean => {
  if (!state) {
    return false;
  }
  const isMultiInheritanceFlow = getIsMultiInheritanceFlow(state);
  const isBasicMultiAccount =
    state.isTrusteeOrEstate === YesNoEmpty.NO &&
    state.isInheritingMultipleAccounts != YesNoEmpty.EMPTY;
  const isIRAEstateSpouse =
    state.isTrusteeOrEstate === YesNoEmpty.YES && getIsIRASpouseExecutor(state);

  return isMultiInheritanceFlow && (isBasicMultiAccount || isIRAEstateSpouse);
};

export const getIsInheritedIraFlow = (state: TriageState): boolean => {
  return state.flowType === FlowTypes.INHERITED_IRA;
};
